<template>
    <div class="tracing-plan-item mb-3">
        <!-- Header del plan -->
        <div class="plan-header" @click="toggleDetails">
            <div class="header-main">
                <i 
                    :class="showDetails ? 'fa fa-caret-down' : 'fa fa-caret-right'"
                    class="toggle-icon"
                ></i>
                <div class="plan-info">
                    <div class="condition-text">
                        {{ displayCondition }}
                    </div>
                </div>
            </div>
            <div class="header-badges">
                <!-- Indicador (Colapsado) -->
                <span 
                    v-if="!showDetails"
                    class="notification-indicator"
                    :class="{ 'enabled': plan.apply_notification }"
                    :title="plan.apply_notification ? 'Notificaciones activas' : 'Notificaciones desactivadas'"
                >
                    <i :class="plan.apply_notification ? 'fa fa-bell' : 'fa fa-bell-slash'"></i>
                </span>

                <!-- Botón (Expandido) -->
                <button 
                    v-else
                    class="notification-btn" 
                    @click.stop="showNotificationConfig = !showNotificationConfig"
                    :class="{ 'active': showNotificationConfig, 'enabled': plan.apply_notification }"
                    v-b-tooltip.hover
                    title="Configurar notificaciones"
                >
                    <i :class="plan.apply_notification ? 'fa fa-bell' : 'fa fa-bell-slash'"></i>
                </button>

                <!-- Cantidad de seguimientos -->
                <span class="tracings-count-badge">
                    <i class="fa fa-clipboard mr-1"></i>
                    {{ plan.tracings_count || 0 }}
                </span>
                <!-- Estado -->
                <span 
                    class="status-badge"
                    :style="getStatusStyle(plan.status)"
                >
                    <i :class="getStatusIcon(plan.status)" class="mr-1"></i>
                    {{ getStatusLabel(plan.status) }}
                </span>
                <!-- Prioridad -->
                <span 
                    class="priority-badge"
                    :style="getPriorityStyle(plan.priority)"
                >
                    <i :class="getPriorityIcon(plan.priority)" class="mr-1"></i>
                    {{ plan.priority }}
                </span>
            </div>
        </div>

        <!-- Detalles del plan -->
        <b-collapse :visible="showDetails">
            <div class="plan-details">
                <!-- Panel de configuración de notificaciones -->
                <notification-config-panel
                    v-if="showNotificationConfig"
                    :apply-notification="plan.apply_notification"
                    :selected-frequencies="selectedFrequencies"
                    :frequency-definitions="notificationFrequencyDefinitions"
                    @toggle-notifications="handleToggleNotifications"
                    @update-frequencies="handleUpdateFrequencies"
                    class="mb-3"
                />

                <!-- Acción Correctiva -->
                <div class="detail-row">
                    <label class="detail-label">Acción Correctiva Sugerida</label>
                    <div class="detail-value">
                        {{ plan.corrective_action || 'No especificada' }}
                    </div>
                </div>

                <b-row class="mt-3">
                    <!-- Responsable (Editable directo) -->
                    <b-col md="4">
                        <div class="detail-row">
                            <label class="detail-label">
                                <i class="fa fa-user mr-1"></i> Responsable
                                <i v-if="savingField === 'responsible_id'" class="fa fa-spinner fa-spin ml-1 text-primary"></i>
                            </label>
                            <el-select
                                v-model="editValues.responsible_id"
                                filterable
                                placeholder="Seleccionar responsable"
                                size="small"
                                class="w-100"
                                :disabled="saving"
                                @change="autoSaveField('responsible_id')"
                            >
                                <el-option
                                    v-for="worker in workers"
                                    :key="worker.id"
                                    :label="worker.fullName || worker.full_name || `${worker.names} ${worker.surnames}`"
                                    :value="worker.id"
                                />
                            </el-select>
                        </div>
                    </b-col>
                    <!-- Prioridad (Editable directo) -->
                    <b-col md="4">
                        <div class="detail-row">
                            <label class="detail-label">
                                <i class="fa fa-flag mr-1"></i> Prioridad
                                <i v-if="savingField === 'priority'" class="fa fa-spinner fa-spin ml-1 text-primary"></i>
                            </label>
                            <el-select
                                v-model="editValues.priority"
                                placeholder="Seleccionar prioridad"
                                size="small"
                                class="w-100"
                                :disabled="saving"
                                @change="autoSaveField('priority')"
                            >
                                <el-option value="Alto" label="Alto" />
                                <el-option value="Medio" label="Medio" />
                                <el-option value="Bajo" label="Bajo" />
                            </el-select>
                        </div>
                    </b-col>
                    <!-- Fecha de vencimiento (Editable directo) -->
                    <b-col md="4">
                        <div class="detail-row">
                            <label class="detail-label">
                                <i class="fa fa-calendar mr-1"></i> Fecha de Vencimiento
                                <i v-if="isOverdue" class="fa fa-exclamation-triangle text-danger ml-1"></i>
                                <i v-if="savingField === 'end_date'" class="fa fa-spinner fa-spin ml-1 text-primary"></i>
                            </label>
                            <el-date-picker
                                v-model="editValues.end_date"
                                type="date"
                                placeholder="Seleccionar fecha"
                                format="dd/MM/yyyy"
                                value-format="yyyy-MM-dd"
                                size="small"
                                class="w-100"
                                :disabled="saving"
                                @change="autoSaveField('end_date')"
                            />
                        </div>
                    </b-col>
                </b-row>

                <!-- Info del item si existe -->
                <div v-if="plan.item" class="mt-3 item-info-section">
                    <label class="detail-label">
                        <i class="fa fa-info-circle mr-1"></i> Información del Hallazgo
                    </label>
                    <div class="item-hierarchy">
                        <span v-if="plan.item.pillar" class="hierarchy-item">
                            <i class="fa fa-circle" :style="{ color: plan.item.pillar.color }"></i>
                            {{ plan.item.pillar.name }}
                        </span>
                        <span v-if="plan.item.element" class="hierarchy-separator">›</span>
                        <span v-if="plan.item.element" class="hierarchy-item">
                            {{ plan.item.element.name }}
                        </span>
                        <span v-if="plan.item.general_item" class="hierarchy-separator">›</span>
                        <span v-if="plan.item.general_item" class="hierarchy-item">
                            {{ plan.item.general_item.name }}
                        </span>
                    </div>
                </div>

                <!-- Sección de Seguimientos (visible al expandir) -->
                <div class="tracings-section mt-4">
                    <div class="tracings-section-header">
                        <h6 class="mb-0">
                            <i class="fa fa-history mr-2"></i>
                            Seguimientos
                            <span class="tracings-count">({{ plan.tracings_count || 0 }})</span>
                        </h6>
                        <b-button 
                            size="sm" 
                            variant="primary"
                            @click.stop="openTracingModal"
                        >
                            <i class="fa fa-plus mr-1"></i> Nuevo Seguimiento
                        </b-button>
                    </div>

                    <!-- Timeline de seguimientos -->
                    <div v-if="plan.tracings && plan.tracings.length > 0" class="tracings-timeline">
                        <div 
                            v-for="tracing in plan.tracings" 
                            :key="tracing.id"
                            class="timeline-item-compact"
                        >
                            <div class="timeline-marker-compact">
                                <i class="fa fa-circle"></i>
                            </div>
                            <div class="timeline-row">
                                <!-- Fecha -->
                                <div class="timeline-col timeline-col-date">
                                    <i class="fa fa-calendar-o"></i>
                                    {{ formatDate(tracing.report_date) }}
                                </div>
                                <!-- Condición reportada -->
                                <div class="timeline-col timeline-col-condition">
                                    {{ tracing.reported_condition || '-' }}
                                </div>
                                <!-- Adjuntos -->
                                <div class="timeline-col timeline-col-attachments">
                                    <template v-if="tracing.attachments && tracing.attachments.length > 0">
                                        <span 
                                            v-for="attachment in tracing.attachments" 
                                            :key="attachment.id"
                                            class="attachment-thumb"
                                            @click.stop="handleDownloadAttachment(attachment)"
                                            :title="attachment.original_name"
                                        >
                                            <img 
                                                v-if="isImage(attachment.original_name)" 
                                                :src="getAttachmentUrl(attachment.path)" 
                                                :alt="attachment.original_name"
                                            />
                                            <i v-else :class="getFileIcon(attachment.original_name)"></i>
                                            <i class="fa fa-download download-icon"></i>
                                        </span>
                                    </template>
                                    <span v-else class="no-attachment">-</span>
                                </div>
                                <!-- Responsable -->
                                <div class="timeline-col timeline-col-responsible">
                                    <i class="fa fa-user-o"></i>
                                    {{ tracing.responsible ? getShortName(tracing.responsible) : '-' }}
                                </div>
                                <!-- Status y acciones -->
                                <div class="timeline-col timeline-col-actions">
                                    <span 
                                        class="timeline-status-compact"
                                        :style="getStatusStyle(tracing.status)"
                                    >
                                        {{ tracing.status }}
                                    </span>
                                    <button 
                                        class="edit-btn-compact"
                                        @click.stop="openEditTracingModal(tracing)"
                                        title="Editar"
                                    >
                                        <i class="fa fa-pencil"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="no-tracings-box">
                        <i class="fa fa-clipboard-list mb-2" style="font-size: 24px; color: #9ca3af;"></i>
                        <p class="mb-0">No hay seguimientos registrados</p>
                        <small class="text-muted">Haga clic en "Nuevo Seguimiento" para agregar uno</small>
                    </div>
                </div>
            </div>
        </b-collapse>

        <!-- Modal para crear seguimiento -->
        <create-tracing-modal
            :visible="showTracingModal"
            :plan="plan"
            :company-id="companyId"
            @close="showTracingModal = false"
            @created="handleTracingCreated"
        />

        <!-- Modal para editar seguimiento -->
        <edit-tracing-modal
            :visible="showEditTracingModal"
            :tracing="selectedTracing"
            :company-id="companyId"
            @close="showEditTracingModal = false; selectedTracing = null"
            @updated="handleTracingUpdated"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import CreateTracingModal from './CreateTracingModal.vue';
import EditTracingModal from './EditTracingModal.vue';
import NotificationConfigPanel from '../../components/NotificationConfigPanel.vue';

export default {
    name: 'TracingPlanItem',
    components: {
        CreateTracingModal,
        EditTracingModal,
        NotificationConfigPanel,
    },
    props: {
        plan: {
            type: Object,
            required: true,
        },
        statusDefinitions: {
            type: Array,
            default: () => [],
        },
        priorityDefinitions: {
            type: Array,
            default: () => [],
        },
        workers: {
            type: Array,
            default: () => [],
        },
        companyId: {
            type: Number,
            default: null,
        },
        expandedPlanId: {
            type: Number,
            default: null,
        },
        notificationFrequencyDefinitions: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            showTracingModal: false,
            showEditTracingModal: false,
            showNotificationConfig: false,
            selectedTracing: null,
            editValues: {
                priority: this.plan.priority,
                responsible_id: (this.plan.responsible && this.plan.responsible.id) || null,
                end_date: this.plan.end_date,
            },
            saving: false,
            savingField: null,
        };
    },
    watch: {
        plan: {
            handler(newPlan) {
                this.editValues = {
                    priority: newPlan.priority,
                    responsible_id: (newPlan.responsible && newPlan.responsible.id) || null,
                    end_date: newPlan.end_date,
                };
            },
            deep: true,
        },
    },

    methods: {
        ...mapActions('skudo/tracings', ['updateActionPlan', 'downloadTracingAttachment']),

        toggleDetails() {
            this.$emit('toggle-plan', this.plan.id);
        },
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            return d.toLocaleDateString('es-CO', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        },
        getStatusDefinition(status) {
            return this.statusDefinitions.find(s => s.value === status) || {};
        },
        getPriorityDefinition(priority) {
            return this.priorityDefinitions.find(p => p.value === priority) || {};
        },
        getStatusStyle(status) {
            const def = this.getStatusDefinition(status);
            return {
                backgroundColor: def.bg_color || '#f3f4f6',
                color: def.text_color || '#6b7280',
                borderColor: def.color || '#6b7280',
            };
        },
        getStatusLabel(status) {
            const def = this.getStatusDefinition(status);
            return def.short_label || def.label || status;
        },
        getStatusIcon(status) {
            const def = this.getStatusDefinition(status);
            return def.icon ? `fa ${def.icon}` : 'fa fa-circle';
        },
        getPriorityStyle(priority) {
            const def = this.getPriorityDefinition(priority);
            return {
                backgroundColor: def.bg_color || '#f3f4f6',
                color: def.text_color || '#6b7280',
                borderColor: def.color || '#6b7280',
            };
        },
        getPriorityIcon(priority) {
            const def = this.getPriorityDefinition(priority);
            return def.icon ? `fa ${def.icon}` : 'fa fa-circle';
        },

        // Auto-save cuando cambia el valor
        async autoSaveField(field) {
            if (this.saving) return;
            
            const newValue = this.editValues[field];
            if (newValue === this.plan[field]) {
                return;
            }

            this.saving = true;
            this.savingField = field;
            try {
                const payload = {
                    company_id: this.companyId,
                    [field]: newValue,
                };

                await this.updateActionPlan({
                    id: this.plan.id,
                    payload,
                });
                // El state se actualiza automáticamente via mutation, no es necesario recargar todo
            } catch (error) {
                // Revertir al valor original en caso de error
                this.editValues[field] = this.plan[field];
                this.$alertError('Error al actualizar');
            } finally {
                this.saving = false;
                this.savingField = null;
            }
        },

        openTracingModal() {
            this.showTracingModal = true;
        },

        openEditTracingModal(tracing) {
            this.selectedTracing = tracing;
            this.showEditTracingModal = true;
        },

        handleTracingCreated() {
            this.showTracingModal = false;
            // Emitir para que Index.vue haga refresh silencioso con los filtros actuales
            this.$emit('updated');
        },

        handleTracingUpdated() {
            this.showEditTracingModal = false;
            this.selectedTracing = null;
            // Emitir para que Index.vue haga refresh silencioso con los filtros actuales
            this.$emit('updated');
        },

        async handleDownloadAttachment(attachment) {
            try {
                const response = await this.downloadTracingAttachment({
                    attachmentId: attachment.id,
                    companyId: this.companyId
                });

                if (response && response.data) {
                    const { base64, mime_type, original_name } = response.data;
                    
                    // Convertir base64 a blob
                    const byteCharacters = atob(base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: mime_type });
                    
                    // Crear URL temporal y descargar
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = original_name;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    this.$alertSuccess('Archivo descargado exitosamente');
                } else {
                    this.$alertError('No se pudo obtener el archivo');
                }
            } catch (error) {
                this.$alertError('Error al descargar el archivo');
            }
        },

        isImage(fileName) {
            if (!fileName) return false;
            const ext = fileName.split('.').pop().toLowerCase();
            return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
        },

        getAttachmentUrl(path) {
            if (!path) return '';
            return `${process.env.VUE_APP_API_URL}/storage/${path}`;
        },

        getFileIcon(fileName) {
            if (!fileName) return 'fa fa-file';
            const ext = fileName.split('.').pop().toLowerCase();
            if (ext === 'pdf') return 'fa fa-file-pdf-o';
            if (['doc', 'docx'].includes(ext)) return 'fa fa-file-word-o';
            if (['xls', 'xlsx'].includes(ext)) return 'fa fa-file-excel-o';
            if (['ppt', 'pptx'].includes(ext)) return 'fa fa-file-powerpoint-o';
            if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return 'fa fa-file-image-o';
            return 'fa fa-file-o';
        },

        truncateFileName(fileName, maxLength = 18) {
            if (!fileName || fileName.length <= maxLength) return fileName;
            const ext = fileName.split('.').pop();
            const name = fileName.substring(0, fileName.lastIndexOf('.'));
            const truncatedName = name.substring(0, maxLength - ext.length - 4) + '...';
            return truncatedName + '.' + ext;
        },

        getShortName(responsible) {
            if (!responsible) return '';
            const fullName = responsible.full_name || `${responsible.names || ''} ${responsible.surnames || ''}`.trim();
            const parts = fullName.split(' ');
            if (parts.length >= 2) {
                return `${parts[0]} ${parts[parts.length - 1]}`;
            }
            return fullName;
        },

        async handleToggleNotifications(newValue) {
            // Optimistic update
            const previousValue = this.plan.apply_notification;
            this.$set(this.plan, 'apply_notification', newValue);

            // Si activamos notificaciones, abrir panel si estaba cerrado
            if (newValue && !this.showNotificationConfig) {
                this.showNotificationConfig = true;
            }

            try {
                await this.updateActionPlan({
                    id: this.plan.id,
                    payload: {
                        company_id: this.companyId,
                        apply_notification: newValue,
                        // Enviar siempre las frecuencias para no perderlas
                        notification_frequencies: this.selectedFrequencies
                    }
                });
                // No emitimos updated aquí para evitar recarga completa que cierre el acordeón/panel
            } catch (error) {
                console.error(error);
                this.$set(this.plan, 'apply_notification', previousValue);
                this.$alertError('Error al actualizar notificaciones');
            }
        },

        async handleUpdateFrequencies(val) {
            this.selectedFrequencies = val; // Actualizar local inmediatamente

            try {
                await this.updateActionPlan({
                    id: this.plan.id,
                    payload: {
                        company_id: this.companyId,
                        apply_notification: true,
                        notification_frequencies: val
                    }
                });
                
                // Actualizar config_notifications en el plan localmente
                const newConfigs = val.map(id => ({
                    id: Math.random(),
                    notification_frequency_id: id
                }));
                this.$set(this.plan, 'config_notifications', newConfigs);

            } catch (error) {
                console.error(error);
                this.$alertError('Error al actualizar frecuencias');
            }
        },

    },
    computed: {
        showDetails() {
            return this.expandedPlanId === this.plan.id;
        },
        displayCondition() {
            return this.plan.corrective_action || 'Sin acción correctiva definida';
        },
        isOverdue() {
            if (!this.plan.end_date) return false;
            const endDate = new Date(this.plan.end_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return endDate < today && this.plan.status !== 'Cerrado';
        },
        selectedFrequencies: {
            get() {
                if (this.plan.config_notifications && Array.isArray(this.plan.config_notifications)) {
                    return this.plan.config_notifications.map(n => n.notification_frequency_id);
                }
                return [];
            },
            set(val) {
                // El setter solo se usa si modificamos selectedFrequencies directamente, pero usamos un handler
            }
        }
    },
};
</script>

<style scoped>
.tracing-plan-item {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
    transition: all 0.2s ease;
}

.tracing-plan-item:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.plan-header {
    padding: 14px 16px;
    background: #fafafa;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    transition: background 0.2s ease;
}

.plan-header:hover {
    background: #f3f4f6;
}

.header-main {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    flex: 1;
    min-width: 0;
}

.toggle-icon {
    color: #6b7280;
    font-size: 1rem;
    margin-top: 2px;
    flex-shrink: 0;
}

.plan-info {
    flex: 1;
    min-width: 0;
}

.condition-text {
    font-size: 0.9rem;
    font-weight: 500;
    color: #374151;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.diagnostic-info {
    margin-top: 4px;
}

.header-badges {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.status-badge,
.priority-badge {
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    border: 1px solid;
    white-space: nowrap;
}

.tracings-badge {
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.tracings-badge.has-tracings {
    background: #dbeafe;
    color: #1e40af;
    border: 1px solid #3b82f6;
}

.tracings-badge.no-tracings {
    background: #f3f4f6;
    color: #6b7280;
    border: 1px solid #d1d5db;
}

.tracings-badge:hover {
    opacity: 0.8;
}

.notification-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    color: #9ca3af;
    padding: 0 6px;
    background: transparent;
    border: none;
}

.notification-indicator.enabled {
    color: #10b981;
    background: transparent;
    border: none;
}

.notification-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #9ca3af;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    padding: 0;
    font-size: 0.8rem;
}

.notification-btn:hover {
    background: #f3f4f6;
    color: #4b5563;
}

.notification-btn.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
}

.notification-btn.enabled {
    color: #10b981;
    background: #ecfdf5;
    border-color: #34d399;
}

.notification-btn.enabled.active {
    background: #10b981;
    color: white;
    border-color: #10b981;
}

.plan-details {
    padding: 16px;
    border-top: 1px solid #e5e7eb;
    background: #ffffff;
}

.detail-row {
    margin-bottom: 0;
}

.detail-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
    display: block;
}

.detail-value {
    font-size: 0.875rem;
    color: #374151;
    padding: 8px 12px;
    background: #f9fafb;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
}

.status-badge-inline {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 500;
}

.item-info-section {
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
}

.item-hierarchy {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #f9fafb;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
    font-size: 0.85rem;
}

.hierarchy-item {
    display: flex;
    align-items: center;
    gap: 4px;
}

.hierarchy-separator {
    color: #9ca3af;
}

/* Tracings Panel */
.tracings-panel {
    padding: 16px;
    background: #f0f9ff;
    border-top: 1px solid #bae6fd;
}

.tracings-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.tracings-header h6 {
    color: #0369a1;
}

.tracings-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.tracing-item {
    background: #ffffff;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid #e0f2fe;
}

.tracing-date {
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 4px;
}

.tracing-condition {
    font-size: 0.875rem;
    color: #374151;
    margin-bottom: 8px;
}

.tracing-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
}

.tracing-responsible {
    color: #6b7280;
}

.tracing-status {
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 500;
}

.tracing-status.status-open {
    background: #dcfce7;
    color: #166534;
}

.tracing-status.status-closed {
    background: #dbeafe;
    color: #1e40af;
}

/* Tracings Count Badge in Header */
.tracings-count-badge {
    display: flex;
    align-items: center;
    padding: 4px 10px;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
}

/* Tracings Section */
.tracings-section {
    border-top: 1px solid #e5e7eb;
    padding-top: 16px;
}

.tracings-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.tracings-section-header h6 {
    color: #374151;
    font-weight: 600;
}

.tracings-count {
    color: #6b7280;
    font-weight: 400;
}

/* Timeline Styles */
.tracings-timeline {
    position: relative;
    padding-left: 24px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.tracings-timeline::before {
    content: '';
    position: absolute;
    left: 7px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e5e7eb;
}

/* Compact Timeline Styles */
.timeline-item-compact {
    position: relative;
    padding-bottom: 8px;
}

.timeline-item-compact:last-child {
    padding-bottom: 0;
}

.timeline-marker-compact {
    position: absolute;
    left: -20px;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.timeline-marker-compact i {
    font-size: 8px;
    color: #3b82f6;
}

.timeline-row {
    display: flex;
    align-items: center;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 0.8rem;
    min-width: 650px;
}

.timeline-row:hover {
    background: #f3f4f6;
}

.timeline-col {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #374151;
}

.timeline-col > i {
    color: #9ca3af;
    font-size: 0.75rem;
}

.timeline-col-date {
    width: 100px;
    flex-shrink: 0;
    color: #6b7280;
    font-weight: 500;
}

.timeline-col-condition {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 100px;
    padding: 0 12px;
}

.timeline-col-attachments {
    display: flex;
    gap: 6px;
    width: 80px;
    flex-shrink: 0;
    justify-content: center;
}

.timeline-col-responsible {
    width: 130px;
    flex-shrink: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0 8px;
}

.timeline-col-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 150px;
    flex-shrink: 0;
    justify-content: flex-end;
}

.timeline-status-compact {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 500;
    white-space: nowrap;
}

.edit-btn-compact {
    background: none;
    border: none;
    padding: 4px 6px;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s;
}

.edit-btn-compact:hover {
    color: #3b82f6;
    background: #e5e7eb;
}

.attachment-thumb {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background: #e5e7eb;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    overflow: hidden;
}

.attachment-thumb:hover {
    background: #d1d5db;
    transform: scale(1.05);
}

.attachment-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.attachment-thumb > i:first-child {
    font-size: 1.1rem;
}

.attachment-thumb .fa-file-pdf-o { color: #dc2626; }
.attachment-thumb .fa-file-word-o { color: #2563eb; }
.attachment-thumb .fa-file-excel-o { color: #16a34a; }
.attachment-thumb .fa-file-image-o { color: #8b5cf6; }
.attachment-thumb .fa-file-o { color: #6b7280; }

.attachment-thumb .download-icon {
    position: absolute;
    bottom: 2px;
    right: 2px;
    font-size: 0.6rem;
    color: #fff;
    background: rgba(59, 130, 246, 0.8);
    padding: 2px;
    border-radius: 2px;
    opacity: 0;
    transition: opacity 0.2s;
}

.attachment-thumb:hover .download-icon {
    opacity: 1;
}

.no-attachment {
    color: #9ca3af;
}

.no-tracings-box {
    text-align: center;
    padding: 24px;
    background: #f9fafb;
    border: 1px dashed #d1d5db;
    border-radius: 8px;
    color: #6b7280;
}

.no-tracings-box p {
    font-size: 0.875rem;
    color: #374151;
}

/* Direct Edit Styles */
.detail-row .el-select,
.detail-row .el-date-editor {
    margin-top: 4px;
}

.detail-row .el-input__inner {
    border-color: #e5e7eb;
}

.detail-row .el-input__inner:hover {
    border-color: #3b82f6;
}

@media (max-width: 768px) {
    .plan-header {
        flex-direction: column;
        gap: 12px;
    }

    .header-badges {
        width: 100%;
        justify-content: flex-start;
    }
}
</style>
